"use client";

import Image from "next/image";
import Link from "next/link";
import { Suspense } from "react";
import { ArrowRight, Truck, Sparkles, Ruler, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

import { InteractiveShowcase } from "@/components/landing/interactive-showcase";
import { FAQSection } from "@/components/landing/faq-section";
import { LandingPricingSection } from "@/components/landing/landing-pricing-section";
import { useLandingCta } from "@/hooks/use-landing-cta";

const TRUCK_SHOWCASE = [
  {
    name: "F-150",
    before: "/showcase/images/before6.webp",
    imageClassName: "object-[center_bottom]",
    wheels: [
      {
        id: "truck-1",
        name: "Off-Road Style",
        image: "/showcase/images/after61.webp",
        thumbnail: "/showcase/images/after61.webp",
      },
      {
        id: "truck-2",
        name: "Sport Black",
        image: "/showcase/images/after62.webp",
        thumbnail: "/showcase/images/after62.webp",
      },
      {
        id: "truck-3",
        name: "Chrome Finish",
        image: "/showcase/images/after63.webp",
        thumbnail: "/showcase/images/after63.webp",
      },
    ],
  },
];

const TRUCK_BENEFITS = [
  {
    title: "Works with lifted trucks",
    description:
      "Whether stock height or lifted, the AI adapts wheel placement to match your truck's stance.",
  },
  {
    title: "Visualize larger wheels",
    description:
      "Preview 20\", 22\", or even 24\" wheels. See how bigger rims look with your current tire setup.",
  },
  {
    title: "Offset considerations",
    description:
      "Get a realistic view of how aggressive offsets will look on your truck before ordering.",
  },
];

const TRUCK_FAQS = [
  {
    question: "Will this work with my lifted truck?",
    answer:
      "Yes. The AI analyzes your truck's actual stance and wheel wells from the photo. Whether you're stock, leveled, or running a 6\" lift, the wheel preview will match your setup.",
  },
  {
    question: "Can I preview larger wheel sizes?",
    answer:
      "Absolutely. Upload any wheel image-whether it's a 20\" stock replacement or a 24\" aftermarket wheel. The system scales and fits it realistically to your truck.",
  },
  {
    question: "What truck makes work with this tool?",
    answer:
      "Any truck. F-150, RAM 1500/2500/3500, Silverado, Sierra, Tacoma, Tundra, Colorado, Ranger-if you can photograph it, we can visualize wheels on it.",
  },
  {
    question: "How accurate is the fitment preview?",
    answer:
      "The preview is designed for visual accuracy-how the wheel will look on your truck. For technical fitment (bolt pattern, offset clearance), always verify with your wheel shop.",
  },
  {
    question: "Can I compare different wheel finishes?",
    answer:
      "Yes. Run multiple previews with the same wheel in different finishes-matte black vs chrome vs bronze. See exactly how each looks on your truck.",
  },
];

export function TruckWheelVisualizerContent() {
  const { heroCtaRef, showStickyCta, ctaText, handleTryItNow } = useLandingCta();

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Truck Wheel Visualizer",
    url: "https://thewheelvisualizer.com/truck-wheel-visualizer",
    description:
      "Upload your truck photo and preview new wheels instantly. AI-powered wheel visualization for pickup trucks.",
    primaryImageOfPage: "https://thewheelvisualizer.com/showcase/images/after61.webp",
    mainEntity: {
      "@type": "SoftwareApplication",
      name: "The Wheel Visualizer",
      operatingSystem: "Web",
      applicationCategory: "GraphicsApplication",
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to visualize wheels on your truck",
    step: [
      {
        "@type": "HowToStep",
        name: "Upload your truck photo",
        text: "Take a side or 3/4 angle photo of your truck and upload it to the visualizer.",
      },
      {
        "@type": "HowToStep",
        name: "Select wheels",
        text: "Choose from our wheel library or upload your own wheel image from any source.",
      },
      {
        "@type": "HowToStep",
        name: "Preview the result",
        text: "See a photorealistic preview with accurate fitment. Download and compare options.",
      },
    ],
  };

  return (
    <div className="bg-[#04060A] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />
        
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
                <Truck className="h-4 w-4 text-white/60" />
                <span className="text-sm text-white/70">Built for trucks</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Truck Wheel Visualizer
                <br />
                <span className="text-white/70">Preview Before You Buy</span>
              </h1>
              
              <p className="text-lg text-white/60 mb-4 leading-relaxed">
                Upload your truck photo and see new wheels in seconds. Works with F-150, RAM, Silverado, Tacoma, and any pickup.
              </p>
              <p className="text-white/80 font-medium mb-8">
                No mounting, no guesswork-just realistic previews.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" ref={heroCtaRef}>
                <Button
                  size="lg"
                  onClick={handleTryItNow}
                  className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
                >
                  {ctaText}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="border-white/20 text-white hover:bg-white/5 h-12 px-8"
                >
                  <Link href="/credits">View Pricing</Link>
                </Button>
              </div>

              {/* Stats */}
              <div className="mt-10 flex flex-wrap gap-8">
                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">All Trucks</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">&lt;60s Preview</span>
                </div>
                <div className="flex items-center gap-2">
                  <Ruler className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">20&quot; to 24&quot;+</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <Image
                  src="/showcase/images/after62.webp"
                  alt="F-150 with aftermarket wheels preview"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority
                />
              </div>
              <div className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl bg-[#0A0C0F] border border-white/10">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-white/60" />
                  <span className="text-sm text-white/80">Lifted & stock trucks</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Truck-specific benefits */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Built for trucks</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Visualize wheels on any pickup
            </h2>
            <p className="text-white/60 mt-4 max-w-2xl">
              From half-tons to heavy-duty, stock to fully built-see exactly how new wheels will look on your truck before spending thousands.
            </p>
          </div>

          <div className="space-y-6">
            {TRUCK_BENEFITS.map((benefit, idx) => (
              <div
                key={benefit.title}
                className="flex gap-6 items-start p-6 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              >
                <div className="text-4xl font-bold text-white/10 shrink-0 w-12">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-white/60 leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive showcase */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Live preview</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              See the difference
            </h2>
            <p className="text-white/60 mt-4">
              Drag the slider to compare stock vs upgraded. Tap thumbnails to switch wheel styles.
            </p>
          </div>

          {TRUCK_SHOWCASE.map((scenario) => (
            <div key={scenario.name} className="space-y-6">
              <InteractiveShowcase
                beforeImage={scenario.before}
                wheels={scenario.wheels}
                imageClassName={scenario.imageClassName}
                priority
              />
            </div>
          ))}
        </div>
      </section>

      {/* Popular truck wheels section */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Popular styles</p>
              <h2 className="text-3xl font-semibold text-white mb-6">
                Truck wheel styles we see most
              </h2>
              <ul className="space-y-4 text-white/70">
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  Off-road beadlocks and method-style wheels
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  Gloss and matte black finishes
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  Chrome and machined face options
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  Bronze and gunmetal for the aggressive look
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  20x9, 20x10, 22x12 and larger sizes
                </li>
              </ul>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
              <Image
                src="/showcase/images/after61.webp"
                alt="Truck with aftermarket wheels"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-[#04060A]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Truck wheel questions
            </h2>
            <p className="text-white/60 mt-4">
              Common questions about visualizing wheels on your truck.
            </p>
          </div>
          <FAQSection faqs={TRUCK_FAQS} />
        </div>
      </section>

      <Suspense fallback={<div className="py-24 px-6 bg-[#0A0C0F]" />}>
        <LandingPricingSection />
      </Suspense>

      {/* Sticky CTA */}
      {showStickyCta && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#05070C]/95 backdrop-blur border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3">
            <button
              onClick={handleTryItNow}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white text-black font-semibold"
            >
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
